import React, { useContext, useEffect, useReducer, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Store } from "../../Store";
import { getError } from "../../utils";
import { api } from "../../Api";

/* ──────────────────────────────────────────────────────────────────────────
   Matching History — admin leaderboard for binary-tree matching points.
   Shows top receivers in a chosen date window with per-receiver counts +
   amounts, plus an inline editor for the daily cap (default 16).
   ────────────────────────────────────────────────────────────────────── */

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true, error: "" };
    case "FETCH_SUCCESS":
      return { ...state, data: action.payload, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const fmtNum = (n) =>
  n === undefined || n === null
    ? "0"
    : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
const fmtMoney = (n) => "৳ " + fmtNum(n);
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayISO = () => toLocalISODate(new Date());
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalISODate(d);
};
const startOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return toLocalISODate(d);
};
const endOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return toLocalISODate(d);
};
const initials = (s) => (s || "?").toString().trim().slice(0, 2).toUpperCase();
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const PRESETS = [
  { key: "today", label: "Today", icon: "fa-sun-o" },
  { key: "yesterday", label: "Yesterday", icon: "fa-moon-o" },
  { key: "7d", label: "This Week", icon: "fa-calendar-o" },
  { key: "30d", label: "Last 30 Days", icon: "fa-calendar" },
  { key: "thisMonth", label: "This Month", icon: "fa-calendar-check-o" },
  { key: "lastMonth", label: "Last Month", icon: "fa-calendar-minus-o" },
];

export default function MatchingHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loading, error, data }, dispatch] = useReducer(reducer, {
    loading: true,
    error: "",
    data: null,
  });

  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [activePreset, setActivePreset] = useState("today");
  const [topN, setTopN] = useState(50);

  // Limit-edit state
  const [limitDraft, setLimitDraft] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      dispatch({ type: "FETCH_REQUEST" });
      try {
        const { data: payload } = await axios.get(
          api +
            `/api/users/admin/matchingleaders?startDate=${startDate}&endDate=${endDate}&limit=${topN}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } },
        );
        if (!cancelled) {
          dispatch({ type: "FETCH_SUCCESS", payload });
          setLimitDraft(String(payload?.config?.matchingDailyLimit ?? ""));
        }
      } catch (err) {
        if (!cancelled)
          dispatch({ type: "FETCH_FAIL", payload: getError(err) });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userInfo.token, startDate, endDate, topN]);

  const applyPreset = (key) => {
    setActivePreset(key);
    const today = todayISO();
    switch (key) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "yesterday": {
        const y = daysAgoISO(1);
        setStartDate(y);
        setEndDate(y);
        break;
      }
      case "7d":
        setStartDate(daysAgoISO(6));
        setEndDate(today);
        break;
      case "30d":
        setStartDate(daysAgoISO(29));
        setEndDate(today);
        break;
      case "thisMonth":
        setStartDate(startOfMonthISO(0));
        setEndDate(today);
        break;
      case "lastMonth":
        setStartDate(startOfMonthISO(-1));
        setEndDate(endOfMonthISO(-1));
        break;
      default:
        break;
    }
  };

  const saveLimit = async () => {
    const val = parseInt(limitDraft, 10);
    if (!Number.isInteger(val) || val < 0 || val > 1000) {
      toast.error("Limit must be a whole number between 0 and 1000");
      return;
    }
    if (val === data?.config?.matchingDailyLimit) {
      toast.info("No change");
      return;
    }
    if (
      !window.confirm(
        `Set daily matching limit to ${val}? This applies to all users from now on.`,
      )
    )
      return;
    try {
      setSavingLimit(true);
      const { data: r } = await axios.put(
        api + "/api/users/admin/matchinglimit",
        { matchingDailyLimit: val },
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      toast.success(r.message || "Saved");
      // Refetch so the leader board re-evaluates capHits against the new limit
      setStartDate((s) => s);
      setEndDate((e) => e);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSavingLimit(false);
    }
  };

  return (
    <div style={S.shell}>
      <Helmet>
        <title>Matching History · Admin</title>
      </Helmet>

      {/* HEADER */}
      <div style={S.hero}>
        <div>
          <h1 style={S.heroTitle}>
            <i className="fa fa-handshake-o" style={{ marginRight: 12 }} />
            Matching Points History
          </h1>
          <p style={S.heroSub}>
            Top receivers of binary-tree matching commissions, with strict
            daily-cap enforcement.
          </p>
        </div>
        <DailyLimitCard
          current={data?.config?.matchingDailyLimit}
          draft={limitDraft}
          onDraft={setLimitDraft}
          onSave={saveLimit}
          saving={savingLimit}
        />
      </div>

      {/* DATE FILTER */}
      <div style={S.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div style={S.sectionTitle}>
            <i
              className="fa fa-filter"
              style={{ marginRight: 8, color: "#6366f1" }}
            />
            Date Range
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {data && (
              <>
                Showing <strong>{fmtNum(data.totals.totalRecords)}</strong>{" "}
                records · <strong>{fmtNum(data.totals.uniqueReceivers)}</strong>{" "}
                receivers
                {loading && (
                  <span style={{ marginLeft: 10, color: "#6366f1" }}>
                    <i className="fa fa-spinner fa-spin" /> loading…
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div style={S.presetRow}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              style={{
                ...S.presetBtn,
                ...(activePreset === p.key ? S.presetBtnActive : {}),
              }}
            >
              <i className={`fa ${p.icon}`} /> {p.label}
            </button>
          ))}
        </div>

        {/* <div style={S.customRow}>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>From</label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => {
                setActivePreset("custom");
                setStartDate(e.target.value);
              }}
              style={S.dateInput}
            />
          </div>
          <i
            className="fa fa-arrow-right"
            style={{
              color: "#94a3b8",
              alignSelf: "flex-end",
              paddingBottom: 10,
            }}
          />
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>To</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayISO()}
              onChange={(e) => {
                setActivePreset("custom");
                setEndDate(e.target.value);
              }}
              style={S.dateInput}
            />
          </div>
          <div style={S.dateGroup}>
            <label style={S.dateLabel}>Top N</label>
            <select
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              style={{ ...S.dateInput, paddingRight: 8 }}
            >
              {[10, 25, 50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          {activePreset === "custom" && (
            <span
              style={{
                ...S.presetBtn,
                ...S.presetBtnActive,
                alignSelf: "flex-end",
                cursor: "default",
              }}
            >
              <i className="fa fa-pencil" /> Custom
            </span>
          )}
        </div> */}
      </div>

      {/* SUMMARY KPIs
      {data && (
        <div style={S.kpiRow}>
          <KpiCard
            icon="fa-list-ol"
            label="Total Awards"
            value={fmtNum(data.totals.totalRecords)}
            sub={`${data.range.startLabel} → ${data.range.endLabel}`}
            color="#6366f1"
          />
          <KpiCard
            icon="fa-money-bill-wave"
            label="Total Amount"
            value={fmtMoney(data.totals.totalAmount)}
            sub={`avg ${fmtNum(
              data.totals.totalRecords
                ? data.totals.totalAmount / data.totals.totalRecords
                : 0
            )} / award`}
            color="#10b981"
          />
          <KpiCard
            icon="fa-users"
            label="Unique Receivers"
            value={fmtNum(data.totals.uniqueReceivers)}
            sub={`top ${data.leaders.length} shown`}
            color="#0ea5e9"
          />
          <KpiCard
            icon="fa-shield"
            label="Hit Daily Cap"
            value={fmtNum(data.totals.cappedReceivers)}
            sub={`out of top ${data.leaders.length} · cap ${data.config.matchingDailyLimit}/day`}
            color="#f59e0b"
            warn={data.totals.cappedReceivers > 0}
          />
        </div>
      )} */}

      {/* DAILY BREAKDOWN BAR CHART */}
      {data && data.daily.length > 0 && <DailyBars daily={data.daily} />}

      {/* LEADERBOARD */}
      <div style={S.card}>
        <div style={S.sectionTitle}>
          <i
            className="fa fa-trophy"
            style={{ marginRight: 8, color: "#f59e0b" }}
          />
          Leaderboard — Most Matching Points Received
        </div>

        {loading && !data ? (
          <div style={S.loadingBox}>
            <div style={S.spinner} />
            <p style={{ marginTop: 12, color: "#64748b" }}>
              Loading leaderboard…
            </p>
          </div>
        ) : error ? (
          <div style={S.errorBox}>
            <i
              className="fa fa-exclamation-triangle"
              style={{ fontSize: 28, color: "#dc2626" }}
            />
            <p style={{ marginTop: 12, color: "#7f1d1d" }}>{error}</p>
          </div>
        ) : !data || data.leaders.length === 0 ? (
          <div style={S.emptyBox}>
            <i
              className="fa fa-inbox"
              style={{ fontSize: 38, color: "#cbd5e1" }}
            />
            <p style={{ marginTop: 12, color: "#94a3b8" }}>
              No matching points awarded between {data?.range.startLabel} and{" "}
              {data?.range.endLabel}.
            </p>
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Receiver</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Matches</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Total</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Days Active</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Max / Day</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Cap Hits</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Designation</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {data.leaders.map((r, i) => {
                  const dailyLimit = data.config.matchingDailyLimit;
                  const overCap = r.maxOnSingleDay > dailyLimit;
                  return (
                    <tr key={r.userId} style={S.tr}>
                      <td style={S.td}>
                        <RankBadge rank={i + 1} />
                      </td>
                      <td style={S.td}>
                        <div style={S.userCell}>
                          <div style={S.avatar}>{initials(r.name)}</div>
                          <div>
                            <div style={S.userName}>{r.name || "—"}</div>
                            <div style={S.userSub}>
                              @{r.username || "—"}
                              {r.isBlocked && (
                                <span style={S.miniBadge("#dc2626")}>
                                  Blocked
                                </span>
                              )}
                              {r.status === false && (
                                <span style={S.miniBadge("#dc2626")}>
                                  Disabled
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          ...S.td,
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#0f172a",
                        }}
                      >
                        {fmtNum(r.totalCount)}
                      </td>
                      <td
                        style={{
                          ...S.td,
                          textAlign: "right",
                          fontWeight: 600,
                          color: "#10b981",
                        }}
                      >
                        {fmtMoney(r.totalAmount)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        {fmtNum(r.daysActive)}
                      </td>
                      <td
                        style={{
                          ...S.td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: overCap
                            ? "#dc2626"
                            : r.maxOnSingleDay >= dailyLimit
                              ? "#f59e0b"
                              : "#0f172a",
                        }}
                        title={
                          overCap ? `Exceeds the ${dailyLimit}/day cap` : ""
                        }
                      >
                        {fmtNum(r.maxOnSingleDay)}
                        {overCap && (
                          <i
                            className="fa fa-exclamation-triangle"
                            style={{ marginLeft: 6 }}
                          />
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {r.capHits > 0 ? (
                          <span style={S.capPill}>
                            <i className="fa fa-shield" /> {r.capHits}
                          </span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        #{r.designation || 0}
                      </td>
                      <td style={S.td}>
                        <Link
                          to={`/admin/userreport/${r.userId}`}
                          style={S.linkBtn}
                        >
                          <i className="fa fa-bar-chart" /> Report
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ResponsiveCSS />
    </div>
  );
}

/* ────────────── components ────────────── */

function DailyLimitCard({ current, draft, onDraft, onSave, saving }) {
  return (
    <div style={S.limitCard}>
      <div style={S.limitLabel}>
        <i
          className="fa fa-shield"
          style={{ marginRight: 6, color: "#f59e0b" }}
        />
        Daily Matching Limit
      </div>
      <div style={S.limitRow}>
        <input
          type="number"
          min={0}
          max={1000}
          step={1}
          value={draft}
          onChange={(e) => onDraft(e.target.value)}
          style={S.limitInput}
        />
        <button onClick={onSave} disabled={saving} style={S.limitSave}>
          {saving ? (
            <>
              <i className="fa fa-spinner fa-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="fa fa-check" /> Save
            </>
          )}
        </button>
      </div>
      <div style={S.limitSub}>
        Currently <strong>{current ?? "—"}</strong> matches/day per user.
        Strictly enforced — admin overrides also blocked once cap is reached.
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color, warn }) {
  return (
    <div
      style={{
        ...S.kpiCard,
        borderTop: `3px solid ${color}`,
        ...(warn ? { background: "#fffbeb" } : {}),
      }}
    >
      <div style={{ ...S.kpiIcon, background: color }}>
        <i className={`fa ${icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.kpiLabel}>{label}</div>
        <div style={S.kpiValue}>{value}</div>
        <div style={S.kpiSub}>{sub}</div>
      </div>
    </div>
  );
}

function DailyBars({ daily }) {
  const max = Math.max(...daily.map((d) => d.count), 1);
  return (
    <div style={S.card}>
      <div style={S.sectionTitle}>
        <i
          className="fa fa-bar-chart"
          style={{ marginRight: 8, color: "#6366f1" }}
        />
        Daily Activity ({daily.length} day{daily.length > 1 ? "s" : ""})
      </div>
      <div style={S.barRow}>
        {daily.map((d) => {
          const pct = (d.count / max) * 100;
          return (
            <div
              key={d.date}
              style={S.barCol}
              title={`${fmtDate(d.date)} · ${d.count} awards · ${fmtMoney(d.total)} · ${d.uniqueReceivers} receivers`}
            >
              <div style={S.barTrack}>
                <div style={{ ...S.barFill, height: `${pct}%` }}>
                  <span style={S.barCount}>{d.count}</span>
                </div>
              </div>
              <div style={S.barLabel}>
                {new Date(d.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RankBadge({ rank }) {
  let bg = "#e2e8f0";
  let color = "#475569";
  if (rank === 1) {
    bg = "linear-gradient(135deg,#fbbf24,#f59e0b)";
    color = "#fff";
  } else if (rank === 2) {
    bg = "linear-gradient(135deg,#94a3b8,#64748b)";
    color = "#fff";
  } else if (rank === 3) {
    bg = "linear-gradient(135deg,#d97706,#92400e)";
    color = "#fff";
  }
  return (
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 800,
      }}
    >
      {rank}
    </div>
  );
}

/* ────────────── styles ────────────── */
const S = {
  shell: {
    padding: 24,
    maxWidth: 1500,
    margin: "0 auto",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 20,
    padding: 24,
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    borderRadius: 16,
    color: "#fff",
    boxShadow: "0 10px 24px rgba(99,102,241,.3)",
  },
  heroTitle: { margin: 0, fontSize: 26, fontWeight: 800 },
  heroSub: { margin: "6px 0 0", fontSize: 13, opacity: 0.9 },

  // Limit card (in hero)
  limitCard: {
    background: "rgba(255,255,255,.95)",
    color: "#0f172a",
    padding: 16,
    borderRadius: 12,
    minWidth: 280,
    boxShadow: "0 6px 18px rgba(0,0,0,.15)",
  },
  limitLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
    marginBottom: 8,
  },
  limitRow: { display: "flex", gap: 8 },
  limitInput: {
    flex: 1,
    border: "2px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 22,
    fontWeight: 800,
    color: "#0f172a",
    outline: "none",
    textAlign: "center",
    fontFamily: "inherit",
  },
  limitSave: {
    padding: "10px 18px",
    background: "linear-gradient(135deg,#10b981,#059669)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  limitSub: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 8,
    lineHeight: 1.4,
  },

  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    border: "1px solid #e2e8f0",
    marginBottom: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.04)",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    display: "inline-flex",
    alignItems: "center",
  },

  // Date filter
  presetRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  presetBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#475569",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 99,
    cursor: "pointer",
  },
  presetBtnActive: {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 3px 8px rgba(99,102,241,.25)",
  },
  customRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    paddingTop: 12,
    borderTop: "1px dashed #e2e8f0",
  },
  dateGroup: { display: "flex", flexDirection: "column", gap: 4 },
  dateLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
  },
  dateInput: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    color: "#0f172a",
    outline: "none",
    fontFamily: "inherit",
  },

  // KPI cards
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  kpiCard: {
    display: "flex",
    gap: 12,
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(15,23,42,.04)",
  },
  kpiIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.2,
    marginTop: 4,
  },
  kpiSub: { fontSize: 11, color: "#64748b", marginTop: 4 },

  // Daily bar chart
  barRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-end",
    paddingTop: 10,
    overflowX: "auto",
    minHeight: 180,
  },
  barCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 50,
    flex: 1,
  },
  barTrack: {
    width: 36,
    height: 140,
    background: "#f1f5f9",
    borderRadius: 8,
    display: "flex",
    alignItems: "flex-end",
    overflow: "hidden",
    position: "relative",
  },
  barFill: {
    width: "100%",
    background: "linear-gradient(to top,#6366f1,#a855f7)",
    minHeight: 6,
    transition: "height .3s",
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  barCount: {
    position: "absolute",
    top: -18,
    fontSize: 11,
    fontWeight: 700,
    color: "#0f172a",
  },
  barLabel: {
    fontSize: 10,
    color: "#64748b",
    fontWeight: 600,
    marginTop: 8,
    whiteSpace: "nowrap",
  },

  // Table
  tableWrap: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "12px 12px",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".5px",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  td: {
    padding: "12px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#1e293b",
    fontSize: 13,
    verticalAlign: "middle",
  },
  tr: { transition: "background .15s" },
  userCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 200 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#06b6d4,#3b82f6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    flexShrink: 0,
  },
  userName: { fontSize: 13, fontWeight: 700, color: "#0f172a" },
  userSub: {
    fontSize: 11,
    color: "#64748b",
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
  },
  miniBadge: (color) => ({
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 99,
    background: color,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: ".5px",
  }),
  capPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#fef3c7",
    color: "#92400e",
    padding: "3px 10px",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
  },
  linkBtn: {
    padding: "6px 12px",
    background: "#eef2ff",
    color: "#4338ca",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 8,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },

  // States
  loadingBox: { textAlign: "center", padding: "60px 20px" },
  errorBox: {
    textAlign: "center",
    padding: "40px 20px",
    background: "#fef2f2",
    borderRadius: 12,
    border: "1px solid #fecaca",
  },
  emptyBox: { textAlign: "center", padding: "60px 20px" },
  spinner: {
    width: 48,
    height: 48,
    border: "5px solid #e2e8f0",
    borderTopColor: "#6366f1",
    borderRadius: "50%",
    animation: "matchspin 0.8s linear infinite",
    margin: "0 auto",
  },
};

function ResponsiveCSS() {
  return (
    <style>{`
      @keyframes matchspin { to { transform: rotate(360deg); } }
      @media (max-width: 640px) {
        h1 { font-size: 20px !important; }
      }
    `}</style>
  );
}
